import { OrgAccessRole } from '../../api-generated/models/orgAccessRole';
import type { MyOrgTeamContextDto, OrgTeamDto } from '../../api-generated/models';

/** `org-detail.component.ts`의 "팀(반)" 섹션에 나열할 항목. */
export interface VisibleTeam {
  id: string;
  name: string;
}

/**
 * org 직속 role(`OrgSummaryDto.role`/`OrgDto.myRole`)이 admin급인지 판정한다 — team으로만
 * 연결되면 null이므로 `!== 'Member'`로만 비교하면 null이 통과해버려 team-only 사용자에게
 * admin 전용 기능이 잘못 노출된다(2026-07-11 sms에서 발견된 실제 버그). sms
 * `org-detail.component.ts`와 commerce `organization-team-members.component.ts`에 완전히 동일한
 * 로컬 함수로 복붙돼 있던 걸 2026-08-19 lib으로 합쳤다(`resolveOrgScopedId`와 동일한 이유 —
 * 2번째 소비처가 생기며 발견된 중복, 코드 리뷰 지적).
 */
export function isOrgAdmin(role: OrgAccessRole | null): boolean {
  return role === OrgAccessRole.Owner || role === OrgAccessRole.Admin;
}

/**
 * "팀(반)" 섹션에 보여줄 team 목록을 계산한다 — org admin은 이 org의 모든 team(`allTeams`,
 * `GET /orgs/:id/teams`), 아니면 본인이 owner인 team만(`myTeamRoles`, `OrgSummaryDto.teams`에서
 * 옵) — owner가 아닌 team은 이 화면에서 아무 것도 못 하므로 목록에서부터 뺀다. `PATCH
 * .../teams/:teamId/members/:memberId/role`을 실제로 인가하는
 * `SpinoffOrgAdminOrTeamOwnerGuard`(백엔드)와 동일한 "org admin 또는 그 team의 owner" 규칙을
 * 프론트에서 미리 반영한 것 — 최종 인가는 여전히 서버가 한다.
 */
export function computeVisibleTeams(
  isAdmin: boolean,
  allTeams: readonly OrgTeamDto[],
  myTeamRoles: readonly MyOrgTeamContextDto[],
): VisibleTeam[] {
  if (isAdmin) {
    return allTeams.map((t) => ({ id: t.id, name: t.name }));
  }
  return myTeamRoles.filter((t) => t.role === OrgAccessRole.Owner).map((t) => ({ id: t.teamId, name: t.teamName }));
}

/** 위 규칙과 동일 — teamId 하나가 관리 가능한지 판정하는 술어(predicate) 형태. */
export function computeCanManageTeam(
  isAdmin: boolean,
  myTeamRoles: readonly MyOrgTeamContextDto[],
): (teamId: string) => boolean {
  if (isAdmin) return () => true;
  const ownedTeamIds = new Set(myTeamRoles.filter((t) => t.role === OrgAccessRole.Owner).map((t) => t.teamId));
  return (teamId: string) => ownedTeamIds.has(teamId);
}
